import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { RoiAssumptions } from '../../shared/roiAssumptions'

/**
 * Client wrappers for the Analytics callables.
 *
 * Server-side aggregation (not a direct Firestore read like most services)
 * because check docs carry the full raw 271 payload — the function projects
 * just the ROI fields and returns pre-bucketed monthly counts. Scoping is
 * enforced from auth claims on the server: admins get the org (optionally a
 * chosen clinic), managers/users get their clinic, impersonating staff get
 * the viewed tenant.
 *
 * v2 contract: one call returns 12 zero-filled calendar months; the page
 * slices ranges client-side, so range switching never refetches.
 */

/** One calendar month of measured counts (see RoiMonth on the server). */
export interface RoiMonth {
  /** YYYY-MM (UTC). */
  month: string
  /** Distinct checks that reached a terminal answer (latest attempt per set). */
  checksRun: number
  /** Of those, definitive payer answers (eligible or ineligible). */
  verifications: number
  medicalVerifications: number
  visionVerifications: number
  /** Identified patient responsibility ≤ the cap's lower bound, pre-summed. */
  identifiedBaseDollars: number
  /** Identified amounts above the floor, raw — clamped client-side to the
   *  practice's `avgVisitResponsibilityUsd` (see deriveRoi). */
  cappableAmounts: number[]
  /** Verified checks that carried a flat dollar amount. */
  identifiedCount: number
  /** Inactive/terminated medical policies caught pre-visit (fixable). */
  inactiveCaught: number
  /** Confirmed out-of-network medical patients caught pre-visit. */
  oonCaught: number
  /** Vision patients caught ineligible pre-service. */
  visionCaught: number
  /** Non-cancelled visits (capped at today) and how many walked in verified. */
  visitsTotal: number
  visitsVerified: number
}

export interface RoiClinicOption {
  clinicId: string
  name: string
}

export interface RoiAnalyticsResult {
  /** YYYY-MM-DD the data runs through (today, UTC). */
  dataThrough: string
  /** True when the org hit a server-side doc cap — figures are a floor, not exact. */
  truncated: boolean
  /** The caller's effective role — drives clinic picker + assumptions save. */
  role: 'admin' | 'manager' | 'user'
  /** The practice's saved assumptions, defaults filling the gaps. */
  assumptions: RoiAssumptions
  /** Active clinics, for the admin clinic picker. */
  clinics: RoiClinicOption[]
  /** Oldest month first; current month (MTD) last. Zero-filled. */
  months: RoiMonth[]
}

export async function getRoiAnalytics(clinicId?: string | null): Promise<RoiAnalyticsResult> {
  const fn = httpsCallable<{ clinicId?: string }, RoiAnalyticsResult>(functions, 'getRoiAnalytics')
  const res = await fn(clinicId ? { clinicId } : {})
  return res.data
}

/** Admin-only: persist assumption changes practice-wide (partial ok). */
export async function updateRoiAssumptions(assumptions: Partial<RoiAssumptions>): Promise<void> {
  const fn = httpsCallable<{ assumptions: Partial<RoiAssumptions> }, { saved: boolean }>(
    functions, 'updateRoiAssumptions',
  )
  await fn({ assumptions })
}
